class QuickAddColorPicker extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.addEventListener('change', this.onSwatchChange.bind(this));
    this.addEventListener('click', this.onSwatchClick.bind(this));
  }

  getProductCard() {
    return this.closest('.product-card-grid');
  }

  isSwiperEmbeddedCard(element) {
    return Boolean(
      element?.closest('.collection-expose__products') ||
        element?.closest('.collection-section__products') ||
        element?.closest('.wr-product-feature__product-inner')
    );
  }

  onSwatchClick(event) {
    const label = event.target.closest('.custom-color-picker__label');
    if (!label || !this.contains(label)) return;

    if (this.isSwiperEmbeddedCard(this)) {
      event.stopPropagation();
    }

    const input = this.getInputForLabel(label);
    if (!input) return;

    if (!input.checked) {
      input.checked = true;
    }

    this.updateProductCard(input);
  }

  getInputForLabel(label) {
    const inputId = label.getAttribute('for');
    if (!inputId) return null;

    try {
      return this.querySelector(`#${CSS.escape(inputId)}`);
    } catch {
      return this.querySelector(`#${inputId}`);
    }
  }

  onSwatchChange(event) {
    const selectedInput = event.target.matches('[data-color-option]')
      ? event.target
      : this.querySelector('input[data-color-option]:checked');

    if (!selectedInput || !selectedInput.hasAttribute('data-color-option'))
      return;

    if (this.isSwiperEmbeddedCard(this)) {
      event.stopPropagation();
    }

    this.updateProductCard(selectedInput);
  }

  updateProductCard(selectedInput) {
    const productCard = this.getProductCard();
    if (!productCard) return;

    const productUrl = selectedInput.dataset.productUrl;
    const productMedia = this.parseJSON(selectedInput.dataset.productMedia);
    const productTitle = selectedInput.dataset.productTitle;
    const firstVariantId = selectedInput.dataset.productFirstVariant;

    if (productUrl) {
      const selectedProductUrl = QuickAddColorPicker.buildProductUrl(
        productUrl,
        firstVariantId
      );
      this.updateProductLink(selectedProductUrl, productCard);
      this.updateQuickAddButton(
        selectedProductUrl,
        firstVariantId,
        productCard
      );
    }

    if (productMedia && productMedia.length > 0) {
      this.updateProductImages(productMedia, productTitle, productCard);
    }

    this.updateProductDetails(selectedInput, productCard);
    this.updateSlimSizes(selectedInput, productCard);
    this.updateSelectedState();
  }

  static buildProductUrl(productUrl, variantId) {
    if (!productUrl) return productUrl;
    if (!variantId) return productUrl;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      return `${url.pathname}${url.search}`;
    } catch {
      const separator = productUrl.includes('?') ? '&' : '?';
      return `${productUrl}${separator}variant=${encodeURIComponent(variantId)}`;
    }
  }

  static getSelectedSwatchInput(card) {
    if (!card) return null;

    const picker = card.querySelector('quick-add-color-picker');
    return (
      picker?.querySelector('input[data-color-option]:checked') ||
      card.querySelector('input[data-color-option]:checked')
    );
  }

  static resolveProductUrlFromCard(card) {
    const selectedInput = QuickAddColorPicker.getSelectedSwatchInput(card);
    if (!selectedInput?.dataset.productUrl) return null;

    return QuickAddColorPicker.buildProductUrl(
      selectedInput.dataset.productUrl,
      selectedInput.dataset.productFirstVariant
    );
  }

  updateQuickAddButton(productUrl, variantId, productCard) {
    productCard
      .querySelectorAll('[data-product-url]')
      .forEach(quickAddButton => {
        quickAddButton.setAttribute('data-product-url', productUrl);
      });

    productCard.dataset.selectedProductUrl = productUrl;

    const variantInput = productCard.querySelector(
      'input[name="id"].product-variant-id'
    );
    if (variantInput && variantId) {
      variantInput.value = variantId;
    }
  }

  updateSlimSizes(selectedInput, productCard) {
    const slimSizes = productCard.querySelector('quick-add-slim-sizes');
    if (!slimSizes) return;

    const variants = this.parseJSON(selectedInput.dataset.product);
    if (!variants?.length) return;

    slimSizes.renderFromVariants(variants);

    const variantInput = productCard.querySelector(
      '.quick-add--slim .product-variant-id'
    );
    const submitButton = productCard.querySelector(
      '.quick-add--slim .quick-add__submit'
    );

    if (variantInput) {
      variantInput.value = '';
      variantInput.disabled = true;
    }

    if (submitButton) {
      submitButton.classList.remove('is-loading');
      submitButton.removeAttribute('aria-disabled');
      QuickAddSlimSizes.updateSubmitButtonState(this, productCard);
    }
  }

  updateProductLink(url, productCard) {
    const link = productCard.querySelector('[data-product-card-link]');
    if (link) {
      link.href = url;
    }
  }

  updateProductImages(mediaArray, productTitle, productCard) {
    const imageContainer = productCard.querySelector(
      '.product-card-grid__image-container'
    );
    if (!imageContainer) return;

    const images = mediaArray
      .filter(media => !media.media_type || media.media_type === 'image')
      .slice(0, 2);

    if (images.length === 0) return;

    const mainImage = images[0];
    const hoverImage = images[1];

    const mainImageEl = imageContainer.querySelector(
      '.product-card-grid__image:not(.product-card-grid__image--hover)'
    );
    const hoverImageEl = imageContainer.querySelector(
      '.product-card-grid__image--hover'
    );

    const imageSizes =
      mainImageEl?.getAttribute('sizes') || hoverImageEl?.getAttribute('sizes');

    if (mainImageEl && mainImage) {
      this.updateImageElement(
        mainImageEl,
        mainImage,
        productTitle,
        imageContainer
      );
    }

    if (hoverImage) {
      if (hoverImageEl) {
        this.updateImageElement(
          hoverImageEl,
          hoverImage,
          productTitle,
          imageContainer,
          imageSizes
        );
        hoverImageEl.style.display = '';
      } else {
        this.createHoverImage(
          imageContainer,
          hoverImage,
          productTitle,
          imageSizes
        );
      }
    } else if (hoverImageEl) {
      hoverImageEl.style.display = 'none';
    }
  }

  normalizeAspectRatio(aspectRatio) {
    const compact = aspectRatio.replace(/\s/g, '');
    const aliases = {
      '64/41': '4/3',
      '3.15/4': '4/5',
      '1/.85': '1/.85',
      '300/480': '5/8',
      '300/504': '5/8',
    };

    return aliases[compact] || compact || '1/1';
  }

  parseAspectRatio(imageContainer) {
    const dataW = parseFloat(imageContainer?.dataset?.aspectRatioW);
    const dataH = parseFloat(imageContainer?.dataset?.aspectRatioH);

    if (dataW > 0 && dataH > 0) {
      return { ratioW: dataW, ratioH: dataH };
    }

    const raw =
      imageContainer?.style?.getPropertyValue('--aspect-ratio') ||
      getComputedStyle(imageContainer).getPropertyValue('--aspect-ratio') ||
      '1/1';
    const aspectRatio = this.normalizeAspectRatio(raw.trim());
    const [ratioW, ratioH] = aspectRatio
      .split('/')
      .map(value => parseFloat(value) || 1);

    return { ratioW, ratioH };
  }

  getImageHeight(width, ratioW, ratioH) {
    return Math.round((width * ratioH) / ratioW);
  }

  buildCardImageUrls(imageBase, ratioW, ratioH) {
    const widths = [200, 400, 600, 800, 1000];
    const defaultWidth = 400;
    const defaultHeight = this.getImageHeight(defaultWidth, ratioW, ratioH);
    const srcset = widths
      .map(width => {
        const height = this.getImageHeight(width, ratioW, ratioH);
        return `${this.getImageUrl(imageBase, `${width}x${height}`)} ${width}w`;
      })
      .join(', ');

    return {
      src: this.getImageUrl(imageBase, `${defaultWidth}x${defaultHeight}`),
      srcset,
      width: defaultWidth,
      height: defaultHeight,
    };
  }

  updateImageElement(
    imgElement,
    mediaData,
    productTitle,
    imageContainer,
    sizes
  ) {
    const preview = mediaData.preview_image || mediaData;
    const imageSrc = preview.src || mediaData.src || '';

    if (!imageSrc) return;

    const imageBase = this.getImageBase(imageSrc);
    const alt = preview.alt || productTitle || '';
    const { ratioW, ratioH } = this.parseAspectRatio(imageContainer);
    const { src, srcset, width, height } = this.buildCardImageUrls(
      imageBase,
      ratioW,
      ratioH
    );

    imgElement.src = src;
    imgElement.srcset = srcset;
    imgElement.alt = alt;
    imgElement.classList.add('product-card-grid__image');
    imgElement.setAttribute('width', String(width));
    imgElement.setAttribute('height', String(height));
    imgElement.classList.add('is-loaded');

    if (sizes) {
      imgElement.setAttribute('sizes', sizes);
    }
  }

  updateProductDetails(selectedInput, productCard) {
    const productTitle = selectedInput.dataset.productTitle;
    const variants = this.parseJSON(selectedInput.dataset.product);

    const titleElement = productCard.querySelector('.product-card-grid__title');
    if (titleElement && productTitle) {
      titleElement.textContent = productTitle;
    }

    if (!variants?.length) return;

    const variant = variants.find(entry => entry.available) || variants[0];
    if (!variant?.price) return;

    const priceElement = productCard.querySelector(
      '.product-card-grid__price-wrapper .price'
    );
    if (!priceElement) return;

    const onSale =
      variant.compare_at_price && variant.compare_at_price > variant.price;

    priceElement.classList.toggle('price--on-sale', Boolean(onSale));

    const regularItem = priceElement.querySelector('.price-item--regular');
    const saleItem = priceElement.querySelector('.price-item--sale');

    if (onSale) {
      if (saleItem)
        saleItem.textContent = window.theme.formatMoney(variant.price);
      if (regularItem)
        regularItem.textContent = window.theme.formatMoney(
          variant.compare_at_price
        );
    } else if (regularItem) {
      regularItem.textContent = window.theme.formatMoney(variant.price);
    }
  }

  createHoverImage(container, mediaData, productTitle, sizes) {
    const preview = mediaData.preview_image || mediaData;
    const imageSrc = preview.src || '';

    if (!imageSrc) return;

    const imageBase = this.getImageBase(imageSrc);
    const alt = preview.alt || productTitle || '';
    const { ratioW, ratioH } = this.parseAspectRatio(container);
    const { src, srcset, width, height } = this.buildCardImageUrls(
      imageBase,
      ratioW,
      ratioH
    );

    const hoverImg = document.createElement('img');
    hoverImg.className =
      'product-card-grid__image product-card-grid__image--hover';
    hoverImg.src = src;
    hoverImg.srcset = srcset;
    hoverImg.setAttribute('width', String(width));
    hoverImg.setAttribute('height', String(height));
    hoverImg.sizes =
      sizes ||
      container
        .querySelector(
          '.product-card-grid__image:not(.product-card-grid__image--hover)'
        )
        ?.getAttribute('sizes') ||
      '';
    hoverImg.alt = alt;
    hoverImg.loading = 'lazy';

    container.appendChild(hoverImg);
  }

  getImageBase(imageSrc) {
    return imageSrc
      .split('?')[0]
      .replace(/_\d+x\d+_crop_center(?=\.[^.]+$)/i, '');
  }

  getImageUrl(imageBase, size) {
    const [width, height] = size.split('x').map(value => parseInt(value, 10));

    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return imageBase;
    }

    try {
      const normalized = imageBase.startsWith('//')
        ? `https:${imageBase}`
        : imageBase;
      const url = new URL(normalized, window.location.origin);
      url.searchParams.set('width', String(width));
      url.searchParams.set('height', String(height));
      url.searchParams.set('crop', 'center');
      return url.toString();
    } catch {
      const extension = imageBase.match(/\.(jpg|jpeg|png|gif|webp)$/i);
      if (!extension) return imageBase;

      return imageBase.replace(
        new RegExp(`\\.${extension[1]}$`, 'i'),
        `_${size}_crop_center.${extension[1]}`
      );
    }
  }

  updateSelectedState() {
    this.querySelectorAll('.custom-color-picker__color').forEach(colorEl => {
      const input = colorEl.querySelector('input[data-color-option]');
      if (input && input.checked) {
        colorEl.classList.add('is-selected');
      } else {
        colorEl.classList.remove('is-selected');
      }
    });
  }

  parseJSON(jsonString) {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('Failed to parse JSON:', e);
      return null;
    }
  }
}

if (!customElements.get('quick-add-color-picker')) {
  customElements.define('quick-add-color-picker', QuickAddColorPicker);
}

class QuickAddSlimSizes extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.onSizeChangeHandler = this.onSizeChange.bind(this);
    this.onSizeClickHandler = this.onSizeClick.bind(this);
    this.onSizeMouseDownHandler = this.onSizeMouseDown.bind(this);

    this.addEventListener('change', this.onSizeChangeHandler);
    this.addEventListener('click', this.onSizeClickHandler);
    this.addEventListener('mousedown', this.onSizeMouseDownHandler);
    this.syncInitialState();
  }

  getProductCard() {
    return this.closest('.product-card-grid');
  }

  getVariantInput() {
    return this.getProductCard()?.querySelector('.quick-add--slim .product-variant-id');
  }

  getSubmitButton() {
    return this.getProductCard()?.querySelector('.quick-add--slim .quick-add__submit');
  }

  isEmbeddedCard() {
    return Boolean(
      this.closest('.collection-expose__products') ||
        this.closest('.collection-section__products') ||
        this.closest('.wr-product-feature__product-inner')
    );
  }

  getInputForLabel(label) {
    const inputId = label.getAttribute('for');
    if (!inputId) return null;

    try {
      return this.querySelector(`#${CSS.escape(inputId)}`);
    } catch {
      return this.querySelector(`#${inputId}`);
    }
  }

  onSizeMouseDown(event) {
    if (!this.isEmbeddedCard()) return;
    if (!event.target.closest('.quick-add-slim__label, .quick-add-slim__input')) return;
    event.stopPropagation();
  }

  static areAllSizesSoldOut(element) {
    const inputs = element?.querySelectorAll('.quick-add-slim__input');
    if (!inputs?.length) return false;

    return !element.querySelector('.quick-add-slim__input:not(:disabled)');
  }

  static updateSubmitButtonState(slimSizes, productCard) {
    const submitButton =
      productCard?.querySelector('.quick-add--slim .quick-add__submit') ||
      slimSizes?.getSubmitButton?.();
    if (!submitButton) return;

    if (slimSizes && QuickAddSlimSizes.areAllSizesSoldOut(slimSizes)) {
      submitButton.disabled = true;
      return;
    }

    submitButton.disabled = false;
    submitButton.removeAttribute('aria-disabled');
  }

  onSizeClick(event) {
    const label = event.target.closest('.quick-add-slim__label');
    if (!label || !this.contains(label)) return;

    if (this.isEmbeddedCard()) {
      event.stopPropagation();
    }

    const input = this.getInputForLabel(label);
    if (!input || input.disabled) return;

    if (!input.checked) {
      input.checked = true;
    }

    this.applySizeSelection(input);
  }

  onSizeChange(event) {
    const input = event.target.matches('input[type="radio"]')
      ? event.target
      : null;
    if (!input || !this.contains(input)) return;

    if (this.isEmbeddedCard()) {
      event.stopPropagation();
    }

    this.applySizeSelection(input);
  }

  applySizeSelection(input) {
    const variantId = input.dataset.variantId;
    const variantInput = this.getVariantInput();
    const submitButton = this.getSubmitButton();

    if (variantInput) {
      variantInput.value = variantId || '';
      variantInput.disabled = !variantId || input.disabled;
    }

    if (submitButton) {
      if (QuickAddSlimSizes.areAllSizesSoldOut(this)) {
        submitButton.disabled = true;
      } else {
        submitButton.disabled = !variantId || input.disabled;
        if (!submitButton.disabled) {
          submitButton.removeAttribute('aria-disabled');
        }
      }
    }
  }

  syncInitialState() {
    const checked = this.querySelector('input[type="radio"]:checked:not(:disabled)');
    if (checked) {
      this.applySizeSelection(checked);
    }
  }

  static escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  static formatSizeLabel(value) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '');

    const aliases = {
      small: 'S',
      s: 'S',
      medium: 'M',
      m: 'M',
      large: 'L',
      l: 'L',
      xlarge: 'XL',
      'x-large': 'XL',
      xl: 'XL',
      '1xlarge': 'XL',
      '2xlarge': 'XXL',
      xxlarge: 'XXL',
      'xx-large': 'XXL',
      xxl: 'XXL',
      '3xlarge': 'XXXL',
      xxxlarge: 'XXXL',
      'xxx-large': 'XXXL',
      xxxl: 'XXXL',
      '4xlarge': 'XXXXL',
      xxxxlarge: 'XXXXL',
      'xxxx-large': 'XXXXL',
      xxxxl: 'XXXXL',
      '5xlarge': 'XXXXXL',
      xxxxxxlarge: 'XXXXXL',
      'xxxxx-large': 'XXXXXL',
      xxxxxl: 'XXXXXL',
    };

    return aliases[normalized] || value;
  }

  static buildSizeOptions(variants, sizeOptionIndex = 0) {
    if (!variants?.length) return [];

    const sizeMap = new Map();

    variants.forEach(variant => {
      const sizeValue =
        variant.options?.[sizeOptionIndex] ??
        variant[`option${sizeOptionIndex + 1}`];
      if (!sizeValue) return;

      const existing = sizeMap.get(sizeValue);
      if (!existing || (variant.available && !existing.available)) {
        sizeMap.set(sizeValue, variant);
      }
    });

    return Array.from(sizeMap.entries()).map(([value, variant]) => ({
      value,
      variantId: variant.id,
      available: Boolean(variant.available),
    }));
  }

  renderFromVariants(variants) {
    const sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex || '0', 10);
    const uid = this.dataset.uid || 'card';
    const sizeOptions = QuickAddSlimSizes.buildSizeOptions(
      variants,
      sizeOptionIndex
    );
    const fieldset = this.querySelector('.quick-add-slim__fieldset');
    const form = this.closest('.quick-add-slim__form');
    const variantInput = this.getVariantInput();
    const submitButton = this.getSubmitButton();

    if (sizeOptions.length <= 1) {
      this.innerHTML = '';
      this.hidden = true;

      const variant = sizeOptions[0]
        ? variants.find(entry => String(entry.id) === String(sizeOptions[0].variantId))
        : variants.find(entry => entry.available) || variants[0];

      if (variantInput && variant) {
        variantInput.value = variant.id;
        variantInput.disabled = !variant.available;
      }

      if (submitButton) {
        submitButton.disabled = !variant?.available;
      }

      if (form) {
        delete form.dataset.requiresSize;
      }

      return;
    }

    this.hidden = false;

    const sizesHtml = sizeOptions
      .map((option, index) => {
        const inputId = `quick-add-slim-size-${uid}-${index}`;
        const disabledAttrs = option.available
          ? ''
          : ' disabled aria-disabled="true"';
        const unavailableClass = option.available ? '' : ' is-unavailable';
        const unavailableLabel = option.available
          ? ''
          : '<span class="visually-hidden">Sold out</span>';

        return `
          <div class="quick-add-slim__size${unavailableClass}">
            <input
              type="radio"
              class="quick-add-slim__input"
              id="${inputId}"
              name="quick-add-slim-size-${QuickAddSlimSizes.escapeHtml(uid)}"
              value="${QuickAddSlimSizes.escapeHtml(option.value)}"
              data-variant-id="${option.variantId}"${disabledAttrs}
            >
            <label class="quick-add-slim__label" for="${inputId}">
              ${QuickAddSlimSizes.escapeHtml(QuickAddSlimSizes.formatSizeLabel(option.value))}
              ${unavailableLabel}
            </label>
          </div>
        `;
      })
      .join('');

    const barHtml = `
      <div class="quick-add-slim__bar">
        <div class="quick-add-slim__options">
          ${sizesHtml}
        </div>
      </div>
    `;

    if (fieldset) {
      const bar = fieldset.querySelector('.quick-add-slim__bar');
      const options = fieldset.querySelector('.quick-add-slim__options');
      if (options) {
        options.innerHTML = sizesHtml;
      } else if (bar) {
        bar.outerHTML = barHtml;
      } else {
        fieldset.innerHTML = barHtml;
      }
    } else {
      this.innerHTML = `
        <fieldset class="quick-add-slim__fieldset" role="radiogroup" aria-label="Size">
          ${barHtml}
        </fieldset>
      `;
    }

    if (variantInput) {
      variantInput.value = '';
      variantInput.disabled = true;
    }

    if (submitButton) {
      QuickAddSlimSizes.updateSubmitButtonState(this, this.getProductCard());
    }

    if (form) {
      form.dataset.requiresSize = 'true';
    }
  }
}

if (!customElements.get('quick-add-slim-sizes')) {
  customElements.define('quick-add-slim-sizes', QuickAddSlimSizes);
}

class QuickAddModal extends ModalDialog {
  constructor() {
    super();
    this.modalContent =
      this.querySelector('[id^="QuickAddInfo-"]') ||
      this.querySelector('.quick-add-modal__content-info');
    this.moved = false; // Track if modal has been moved to body

    this.addEventListener('product-info:loaded', ({ target }) => {
      target.addPreProcessCallback(this.preprocessHTML.bind(this));
    });

    const refreshGallery = () =>
      requestAnimationFrame(() => this.refreshQuickAddGallery());

    this.addEventListener('color-picker:product-changed', refreshGallery);
    this.addEventListener('media-gallery:updated', refreshGallery);

    window.ProductMobileZoom?.bindQuickAddModal?.(this);
  }

  // Override connectedCallback to prevent moving to body on page load
  connectedCallback() {
    // Don't move to body immediately - only when shown
    // This prevents Safari crashes with multiple featured collections
    return;
  }

  // Move modal to body only when it's actually shown
  moveToBody() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  disableScroll() {
    document.body.classList.add('overflow-hidden');
  }
  enableScroll() {
    document.body.classList.remove('overflow-hidden');
  }

  resolveProductFetchUrl(opener) {
    const card = opener?.closest('.product-card-grid');
    return (
      QuickAddColorPicker.resolveProductUrlFromCard(card) ||
      card?.dataset?.selectedProductUrl ||
      opener?.getAttribute('data-product-url')
    );
  }

  show(opener) {
    const cartDrawer = document.querySelector('cart-drawer');
    cartDrawer?.cancelPendingOpen?.();
    if (cartDrawer?.classList.contains('active')) {
      cartDrawer.closeImmediate();
    }

    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
      const pendingOnClosed = this._pendingOnClosed;
      this._pendingOnClosed = null;
      this.classList.remove('is-opening');
      this.modalContent.innerHTML = '';
      document.body.classList.remove('overflow-hidden');
      window.pauseAllMedia();
      pendingOnClosed?.({ interrupted: true });
    }

    // Move to body only when showing
    this.moveToBody();
    opener.setAttribute('aria-disabled', 'true');
    opener.disabled = true;
    opener.classList.add('is-loading');

    const productUrl = this.resolveProductFetchUrl(opener);
    if (!productUrl) {
      opener.removeAttribute('aria-disabled');
      opener.disabled = false;
      opener.classList.remove('is-loading');
      return;
    }

    opener.setAttribute('data-product-url', productUrl);

    fetch(productUrl, { credentials: 'same-origin', cache: 'no-store' })
      .then(response => response.text())
      .then(responseText => {
        const responseHTML = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );
        const productElement = responseHTML.querySelector('product-info');

        this.preprocessHTML(productElement);
        this.modalContent.classList.remove('is-content-ready');
        HTMLUpdateUtility.setInnerHTML(
          this.modalContent,
          productElement.outerHTML
        );
        this.initializeQuickAddGallery();
        window.KlaviyoBis?.updateVisibility(
          this.modalContent.querySelector('product-info')
        );

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        // Set display first, then trigger animation on next frame
        this.classList.add('is-opening');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // super.show() will set the 'open' attribute and handle overflow
            super.show(opener);
            this.disableScroll();
            requestAnimationFrame(() => {
              this.modalContent.classList.add('is-content-ready');
            });
            this.refreshQuickAddGallery();
          });
        });
      })
      .finally(() => {
        opener.removeAttribute('aria-disabled');
        opener.disabled = false;
        opener.classList.remove('is-loading');
      });
  }

  hide(preventFocus = false, options = {}) {
    const cartNotification =
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer');
    if (cartNotification) cartNotification.setActiveElement(this.openedBy);

    if (preventFocus) this.openedBy = null;

    this.enableScroll();
    this.modalContent.classList.remove('is-content-ready');

    // Remove the 'open' attribute to trigger close animation
    this.removeAttribute('open');

    clearTimeout(this._hideTimeout);
    this._pendingOnClosed = options.onClosed || null;

    // Wait for animation to complete before hiding with display: none
    this._hideTimeout = setTimeout(() => {
      this._hideTimeout = null;
      const onClosed = this._pendingOnClosed;
      this._pendingOnClosed = null;
      this.classList.remove('is-opening');
      this.modalContent.innerHTML = '';
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia();
      onClosed?.();
    }, 600); // Match --duration-long (600ms) from CSS
  }

  preprocessHTML(productElement) {
    productElement.classList.forEach(classApplied => {
      if (classApplied.startsWith('color-') || classApplied === 'gradient')
        this.modalContent.classList.add(classApplied);
    });
    this.preventDuplicatedIDs(productElement);
    this.removeDOMElements(productElement);
    this.prepareQuickAddProduct(productElement);
    this.removeGalleryListSemantic(productElement);
    this.updateImageSizes(productElement);
    this.preventVariantURLSwitching(productElement);
    this.configureQuickAddGallery(productElement);
  }

  prepareQuickAddProduct(productElement) {
    productElement.querySelector('product-zoom-modal')?.remove();

    productElement
      .querySelectorAll('link[rel="stylesheet"], script[src]')
      .forEach(asset => {
        const href =
          asset.getAttribute('href') || asset.getAttribute('src') || '';
        if (href.includes('product-zoom-modal')) {
          asset.remove();
        }
      });

    const product = productElement.querySelector('.product');
    if (product) product.classList.add('product--quick-add');
  }

  preventVariantURLSwitching(productElement) {
    productElement.setAttribute('data-update-url', 'false');
  }

  configureQuickAddGallery(productElement) {
    const gallery = productElement.querySelector('media-gallery');
    if (!gallery) return;

    gallery.classList.add('product-media-gallery--quick-add');

    if (!gallery.dataset.quickAddZoom) {
      gallery.dataset.quickAddZoom = 'inline';
    }

    const thumbnails = gallery.querySelector(
      '.product-media-gallery__thumbnails'
    );
    if (thumbnails) thumbnails.remove();

    const mainSlideshow = gallery.querySelector('.product-media-gallery__main');
    if (mainSlideshow) {
      mainSlideshow.removeAttribute('thumbnails');
      mainSlideshow.setAttribute('grab-cursor', 'false');
      return;
    }

    const gridSlideshow = gallery.querySelector(
      '.product-media-gallery__grid-host'
    );
    if (!gridSlideshow) return;

    gridSlideshow.removeAttribute('disableOn');
    gridSlideshow.setAttribute('create-elements', 'true');
    gridSlideshow.setAttribute('show-navigation', 'true');
    gridSlideshow.setAttribute('show-pagination', 'true');
    gridSlideshow.setAttribute('slides-per-view', '1');
    gridSlideshow.setAttribute('effect', 'fade');
    gridSlideshow.setAttribute('grab-cursor', 'false');
  }

  initializeQuickAddGallery() {
    this.modalContent
      .querySelectorAll('swiper-slideshow')
      .forEach(slideshow => {
        if (typeof slideshow.syncLayoutMode === 'function') {
          slideshow.syncLayoutMode();
        }
      });

    this.scheduleMobileZoomRefresh();
  }

  scheduleMobileZoomRefresh() {
    const refresh = () => {
      window.ProductMobileZoom?.bindQuickAddModal?.(this);
      document.dispatchEvent(new CustomEvent('mobile-zoom:refresh'));
      window.ProductMobileZoom?.refresh?.();
    };

    refresh();
    requestAnimationFrame(refresh);
    window.setTimeout(refresh, 300);
  }

  refreshQuickAddGallery() {
    const slideshow = this.modalContent.querySelector(
      '.product-media-gallery__main, .product-media-gallery__grid-host'
    );
    if (!slideshow) return;

    if (typeof slideshow.refresh === 'function') {
      slideshow.refresh();
    } else {
      const swiper = slideshow.getSwiper?.();
      if (swiper) swiper.update();
    }

    this.scheduleMobileZoomRefresh();
  }

  removeDOMElements(productElement) {
    const pickupAvailability = productElement.querySelector(
      'pickup-availability'
    );
    if (pickupAvailability) pickupAvailability.remove();

    const productModal = productElement.querySelector('product-modal');
    if (productModal) productModal.remove();

    const modalDialog = productElement.querySelectorAll('modal-dialog');
    if (modalDialog) modalDialog.forEach(modal => modal.remove());
  }

  preventDuplicatedIDs(productElement) {
    const sectionId = productElement.dataset.section;
    if (!sectionId) return;

    const oldId = sectionId;
    const newId = `quickadd-${sectionId}`;

    productElement.innerHTML = productElement.innerHTML.replaceAll(
      oldId,
      newId
    );
    Array.from(productElement.attributes).forEach(attribute => {
      if (attribute.value.includes(oldId)) {
        productElement.setAttribute(
          attribute.name,
          attribute.value.replace(oldId, newId)
        );
      }
    });

    productElement.dataset.originalSection = sectionId;
    productElement.dataset.section = newId;
  }

  removeGalleryListSemantic(productElement) {
    const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
    if (!galleryList) return;

    galleryList.setAttribute('role', 'presentation');
    galleryList
      .querySelectorAll('[id^="Slide-"]')
      .forEach(li => li.setAttribute('role', 'presentation'));
  }

  updateImageSizes(productElement) {
    const product = productElement.querySelector('.product');
    const desktopColumns = product?.classList.contains('product--columns');
    if (!desktopColumns) return;

    const mediaImages = product.querySelectorAll('.product__media img');
    if (!mediaImages.length) return;

    let mediaImageSizes =
      '(min-width: 1000px) 715px, (min-width: 700px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

    if (product.classList.contains('product--medium')) {
      mediaImageSizes = mediaImageSizes.replace('715px', '605px');
    } else if (product.classList.contains('product--small')) {
      mediaImageSizes = mediaImageSizes.replace('715px', '495px');
    }

    mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
  }
}

if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', QuickAddModal);
}

if (!customElements.get('quick-add-modal-mobile')) {
  customElements.define(
    'quick-add-modal-mobile',
    class QuickAddModalMobile extends QuickAddModal {
      constructor() {
        super();
        this.sheetContent = this.querySelector('.quick-add-modal__content');
        this.sheetHandle = this.querySelector('.quick-add-modal__sheet-handle');
        this.dragState = null;
        this.dismissThreshold = 96;
        this.onSheetPointerDown = this.onSheetPointerDown.bind(this);
        this.onSheetPointerMove = this.onSheetPointerMove.bind(this);
        this.onSheetPointerUp = this.onSheetPointerUp.bind(this);
        this.initSheetInteractions();
      }

      initSheetInteractions() {
        this.sheetHandle?.addEventListener(
          'pointerdown',
          this.onSheetPointerDown
        );
      }

      onSheetPointerDown(event) {
        if (!this.hasAttribute('open') || this.dragState) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (!event.target.closest('.quick-add-modal__sheet-handle')) return;

        this.dragState = {
          pointerId: event.pointerId,
          startY: event.clientY,
          startX: event.clientX,
          currentY: 0,
          dragging: true,
        };

        this.sheetContent.classList.add('is-dragging');
        this.sheetContent.setPointerCapture(event.pointerId);
        window.addEventListener('pointermove', this.onSheetPointerMove, {
          passive: false,
        });
        window.addEventListener('pointerup', this.onSheetPointerUp);
        window.addEventListener('pointercancel', this.onSheetPointerUp);
      }

      onSheetPointerMove(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId)
          return;

        const deltaY = event.clientY - this.dragState.startY;
        event.preventDefault();

        const offsetY = Math.max(0, deltaY);
        this.dragState.currentY = offsetY;
        this.sheetContent.style.transform = `translateY(${offsetY}px)`;

        const progress = Math.min(offsetY / (this.dismissThreshold * 2), 1);
        this.style.setProperty(
          '--sheet-backdrop-opacity',
          String(1 - progress * 0.35)
        );
      }

      onSheetPointerUp(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId)
          return;

        window.removeEventListener('pointermove', this.onSheetPointerMove);
        window.removeEventListener('pointerup', this.onSheetPointerUp);
        window.removeEventListener('pointercancel', this.onSheetPointerUp);

        this.sheetContent.classList.remove('is-dragging');

        try {
          this.sheetContent.releasePointerCapture(event.pointerId);
        } catch (_) {
          /* noop */
        }

        const shouldDismiss = this.dragState.currentY >= this.dismissThreshold;
        this.dragState = null;
        this.style.removeProperty('--sheet-backdrop-opacity');

        if (shouldDismiss) {
          this.resetSheetPosition(false);
          this.hide();
          return;
        }

        this.resetSheetPosition(true);
      }

      resetSheetPosition(animate) {
        if (!this.sheetContent) return;

        if (animate) {
          this.sheetContent.style.transition = '';
          this.sheetContent.style.transform = '';
          return;
        }

        this.sheetContent.style.transition = 'none';
        this.sheetContent.style.transform = '';
        void this.sheetContent.offsetHeight;
        this.sheetContent.style.transition = '';
      }

      hide(preventFocus = false) {
        this.resetSheetPosition(false);
        super.hide(preventFocus);
      }
    }
  );
}
